import {
  ApiMeasureType,
  ApiReturnAllSensor,
  ApiReturnMeasurePoint,
  ApiReturnMeasuresValidated,
} from '@highimpact-frontend/api-types';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { FormControl, FormHelperText, Input } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import assert from 'assert';
import * as A from '@highimpact/data-utils/array';
import * as R from '@highimpact/data-utils/record';
import { forwardRef, useMemo, useState, type LegacyRef } from 'react';
import { useParams } from 'react-router-dom';
import MeasureService from '../../../api/services/data/measures/MeasureService';
import ConfirmModal from '../../../components/utils/confirmModal/ConfirmModal';
import PermissionDecoratorV2 from '../../../components/utils/permission/PermissionDecoratorV2';
import StateDecorator, {
  StateDecoratorWithoutRef,
} from '../../../components/utils/state/StateDecorator';
import ValueFormComponent from '../../../components/ValueFormComponent';
import { isCachedDataDataState } from '../../../store/data/data';
import {
  getKeyFromParamsAndId,
  resetCachedDataForUrlByPattern,
} from '../../../store/data/slice';
import { isDataState } from '../../../store/data/state';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { groupBy, mapFilter, sort } from '../../../utils/array';
import { useModal } from '../../../utils/hooks/useModal';
import { useSubmit } from '../../../utils/hooks/useSubmit';
import { ensureNumber } from '../../../utils/number';
import {
  concat,
  contramap,
  dateOrder,
  reverse,
  stringOrder,
} from '../../../utils/Order';
import SiteDecorator from '../utils/SiteDecorator';

type Measure = ApiReturnMeasuresValidated['data'][number];

interface InputGroup {
  campaign: string;
  thematic: string;
  inputDate: Date;
  values: InputValue[];
}

interface CampaignWithThematics {
  campaignId: number;
  campaign: string;
  thematic: string;
  startDate: string;
  endDate: string;
}

interface InputValue {
  id: number;
  siteId: number;
  deviceId: number;
  measuringPoint: { id: number; name: string };
  measureType: { id: number; name: string };
  timestamp: Date;
  value: number | string;
}

/**
 * @description Formats a Date object to a string in the YYYY-MM-DD format.
 */
function extractDate(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * @description Ensure that an input is a valid number.
 */
function isValidNumber(value: number | string): boolean {
  if (value === '') return false;
  const num = Number(value);
  return typeof num === 'number' && !isNaN(num);
}

/**
 * @description Split a list of measures into groups corresponding to the measures' timestamp in the YYYY-MM-DD format (omitting the time).
 */
function groupMeasuresByDate(measures: Measure[]): Record<string, Measure[]> {
  return groupBy(measures, ({ horodate }) => extractDate(new Date(horodate)));
}

function createInputValue(
  id: number,
  siteId: number,
  deviceId: number,
  measuringPoint: { id: number; name: string },
  measureType: { id: number; name: string },
  timestamp: Date,
  value: number | string
): InputValue {
  return {
    id,
    siteId,
    deviceId,
    measuringPoint,
    measureType,
    timestamp,
    value,
  };
}

function createInputGroup(
  campaign: string,
  thematic: string,
  inputDate: Date,
  values: InputValue[]
): InputGroup {
  return {
    campaign,
    thematic,
    inputDate,
    values,
  };
}

function updateInputValueAtIndex(
  inputGroup: InputGroup,
  index: number,
  newValue: string | number
): InputGroup {
  if (index < 0 || index >= inputGroup.values.length) {
    return inputGroup;
  }

  const updatedValues = [...inputGroup.values];

  const oldValue = updatedValues[index];
  updatedValues[index] = createInputValue(
    oldValue.id,
    oldValue.siteId,
    oldValue.deviceId,
    oldValue.measuringPoint,
    oldValue.measureType,
    oldValue.timestamp,
    newValue
  );

  return createInputGroup(
    inputGroup.campaign,
    inputGroup.thematic,
    inputGroup.inputDate,
    updatedValues
  );
}

function removeInputValueById(inputGroup: InputGroup, id: number) {
  const updatedValues = inputGroup.values.filter((ig) => ig.id !== id);
  return createInputGroup(
    inputGroup.campaign,
    inputGroup.thematic,
    inputGroup.inputDate,
    updatedValues
  );
}

const descendingDateOrder = reverse(dateOrder);
const groupInputDescendingDateOrder = contramap(
  descendingDateOrder,
  (ig: InputGroup) => ig.inputDate
);
const groupInputCampaignOrder = contramap(
  stringOrder,
  (ig: InputGroup) => ig.campaign
);
const groupInputOrder = concat(
  groupInputDescendingDateOrder,
  groupInputCampaignOrder
);

function getMeasurePointName(
  deviceId: number,
  siteId: number,
  measurePointByIds: Record<string, ApiReturnMeasurePoint>,
  devicesById: Record<string, ApiReturnAllSensor>
): string {
  // Find the measure_point that references the device_site that references the deviceId and siteId
  const matchedMeasurePoint = R.values(measurePointByIds).find(
    (mp) => mp.device_site?.device_id === deviceId && mp.site_id === siteId
  );

  return (
    matchedMeasurePoint?.name ??
    devicesById[deviceId]?.name ??
    `Station de mesure inconnue ${deviceId}`
  );
}

function structureData(
  campaigns: CampaignWithThematics[],
  measuresByCampaignsAndDate: Record<string, Record<string, Measure[]>>,
  siteId: number,
  devicesById: Record<string, ApiReturnAllSensor>,
  measurePointsById: Record<string, ApiReturnMeasurePoint>,
  measureTypesById: Record<string, ApiMeasureType>
): InputGroup[] {
  const manualSensorIds = Object.values(devicesById)
    .filter((d) => d.type === 'manual_sensor')
    .map((d) => d.id);

  const unsorted = campaigns
    .filter((c) => measuresByCampaignsAndDate[c.campaign] !== undefined)
    .flatMap((c) => {
      const groups = measuresByCampaignsAndDate[c.campaign];
      return Object.entries(groups).map(([date, measures]) =>
        createInputGroup(
          c.campaign,
          c.thematic,
          new Date(date),
          measures
            .filter((ms) => manualSensorIds.includes(ms.device_id!))
            .map((m) =>
              createInputValue(
                m.id,
                siteId,
                m.device_id!,
                {
                  id: m.device_id!,
                  name: getMeasurePointName(
                    m.device_id!,
                    siteId,
                    measurePointsById,
                    devicesById
                  ),
                },
                {
                  id: m.measure_type_id,
                  name:
                    measureTypesById[m.measure_type_id]?.name ??
                    `Type de mesure inconnu ${m.measure_type_id}`,
                },
                new Date(m.horodate),
                m.value
              )
            )
        )
      );
    });
  return sort(unsorted, groupInputOrder);
}

function Row(props: { row: InputGroup }) {
  const { row } = props;
  const [open, setOpen] = useState(false);
  const [editedRowIndex, setEditedRowIndex] = useState<number | null>(null);
  const [statefulRow, setStatefulRow] = useState(row);
  const [invalidRowIndices, setInvalidRowIndices] = useState(new Set<number>());

  const deleteModal = useModal<{
    callback: () => void;
    message: string;
    target: string;
    id: number;
  }>();

  const { submit, state } = useSubmit<void>();

  const handleClick = (rowIndex: number) => {
    setEditedRowIndex(rowIndex);
  };

  const handleBlur: React.FocusEventHandler<HTMLInputElement> = () => {
    setEditedRowIndex(null);
  };

  type KeyPressEvent = React.KeyboardEvent<HTMLInputElement>;

  const handleEdit = (index: number, value: string) => {
    if (!isValidNumber(value)) {
      setInvalidRowIndices((invalidRowIndices) => {
        const copy = new Set(invalidRowIndices);
        copy.add(index);
        return copy;
      });
    } else {
      setInvalidRowIndices((invalidRowIndices) => {
        const copy = new Set(invalidRowIndices);
        copy.delete(index);
        return copy;
      });
    }
    setStatefulRow(updateInputValueAtIndex(statefulRow, index, value));
  };

  const handleKeyPress = (
    e: KeyPressEvent,
    index: number,
    id: number,
    siteId: number,
    deviceId: number,
    measurePointId: number,
    measureTypeId: number,
    horodate: string
  ) => {
    if (e.key === 'Enter') {
      const value = e.currentTarget.value;
      setEditedRowIndex(null);
      handleEdit(index, value);
      if (isValidNumber(value)) {
        submit({
          callbacks: [
            async () => {
              await MeasureService.put(id, {
                site_id: siteId,
                device_id: deviceId,
                measure_type_id: measureTypeId,
                measure_point_id: measurePointId,
                value: Number.parseFloat(value),
                horodate: new Date(horodate).toISOString(),
              });
              setStatefulRow(
                updateInputValueAtIndex(statefulRow, index, value)
              );
            },
          ],
          successMessage: 'La donnée a été mise à jour avec succès',
          errorMessages: {
            anyError: 'Une erreur est survenue lors de la mise à jour',
            unicityError: '',
          },
        });
      }
    }
  };

  const handleDelete = (id: number) => {
    submit({
      callbacks: [
        async () => {
          await MeasureService.delete(id);
          setStatefulRow(removeInputValueById(statefulRow, id));
          deleteModal.close();
        },
      ],
      successMessage: 'La donnée a été supprimée avec succès',
      errorMessages: {
        anyError: 'Une erreur est survenue lors de la suppression',
        unicityError: '',
      },
    });
  };

  if (statefulRow.values.length === 0) {
    return null;
  }

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell style={{ width: '70px', boxSizing: 'border-box' }}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {statefulRow.campaign}
        </TableCell>
        <TableCell>{statefulRow.thematic}</TableCell>
        <TableCell style={{ maxWidth: '130px' }} align="right">
          {statefulRow.inputDate.toLocaleDateString()}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                Données saisies le {statefulRow.inputDate.toLocaleDateString()}
              </Typography>
              <Table size="small" aria-label="purchases">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{ borderBottom: '1px solid rgba(224, 224, 224, 1)' }}
                    >
                      Station de mesure
                    </TableCell>
                    <TableCell
                      sx={{ borderBottom: '1px solid rgba(224, 224, 224, 1)' }}
                      align="right"
                    >
                      Mesure
                    </TableCell>
                    <TableCell
                      sx={{ borderBottom: '1px solid rgba(224, 224, 224, 1)' }}
                      align="right"
                    >
                      Horodatage
                    </TableCell>
                    <TableCell
                      sx={{ borderBottom: '1px solid rgba(224, 224, 224, 1)' }}
                      align="right"
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statefulRow.values.map((valueRow, index) => (
                    <TableRow key={valueRow.timestamp.getTime() + valueRow.id}>
                      <TableCell component="th" scope="row">
                        {valueRow.measuringPoint.name}
                      </TableCell>
                      <TableCell
                        align="right"
                        onClick={() => handleClick(index)}
                        sx={
                          editedRowIndex !== index &&
                          invalidRowIndices.has(index)
                            ? { borderColor: 'red' }
                            : undefined
                        }
                      >
                        {editedRowIndex === index ? (
                          <PermissionDecoratorV2
                            item={'measure_data_edit_field'}
                          >
                            <FormControl>
                              <Input
                                error={invalidRowIndices.has(index)}
                                type="text"
                                value={valueRow.value}
                                onKeyUp={(e) =>
                                  handleKeyPress(
                                    e as KeyPressEvent,
                                    index,
                                    valueRow.id,
                                    valueRow.siteId,
                                    valueRow.deviceId,
                                    valueRow.measuringPoint.id,
                                    valueRow.measureType.id,
                                    valueRow.timestamp.toISOString() // sent as UTC to server
                                  )
                                }
                                onChange={(e) =>
                                  handleEdit(index, e.currentTarget.value)
                                }
                                onBlur={handleBlur}
                                autoFocus
                                aria-describedby="my-helper-text"
                                sx={{ width: '265px' }}
                              />
                              <FormHelperText id="my-helper-text">
                                Entrer une valeur et appuyer sur entrée
                              </FormHelperText>
                            </FormControl>
                          </PermissionDecoratorV2>
                        ) : (
                          valueRow.value
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {
                          valueRow.timestamp.toLocaleString() /* displayed according to the user's timezone */
                        }
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Supprimer l'entrée">
                          <PermissionDecoratorV2
                            item={'measure_data_delete_button'}
                          >
                            <IconButton
                              aria-label="delete"
                              onClick={() => {
                                const valueFound = row.values.find(
                                  (iv) => iv.id === valueRow.id
                                );
                                deleteModal.open({
                                  callback: () => handleDelete(valueRow.id),
                                  message: `Voulez-vous supprimer la donnée ${
                                    valueFound?.value
                                  } horodatée au ${
                                    valueFound
                                      ? new Date(
                                          valueFound.timestamp
                                        ).toLocaleString()
                                      : 'Date inconnue'
                                  } sur la station de mesure`,
                                  target: valueFound?.measuringPoint.name ?? '',
                                  id: valueRow.id,
                                });
                              }}
                              disabled={state === 'Loading'}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </PermissionDecoratorV2>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      <ConfirmModal
        onCancel={deleteModal.close}
        onConfirm={() => {
          if (deleteModal.params?.id) {
            handleDelete(deleteModal.params.id);
          }
        }}
        open={deleteModal.isOpen}
      >
        <p>
          {deleteModal.params?.message!}{' '}
          <span className="uk-text-bold">{deleteModal.params?.target}</span> ?
        </p>
      </ConfirmModal>
    </>
  );
}

interface CollapsibleTableProps {
  siteId: number;
  campaigns: CampaignWithThematics[];
  devicesById: Record<string, ApiReturnAllSensor>;
  measurePointsById: Record<string, ApiReturnMeasurePoint>;
  measureTypesById: Record<string, ApiMeasureType>;
}

function CollapsibleTable({
  siteId,
  campaigns,
  devicesById,
  measurePointsById,
  measureTypesById,
}: CollapsibleTableProps) {
  const deviceIds = mapFilter(R.keys(devicesById), ensureNumber);
  const urlToCampaign = useMemo(() => {
    return campaigns.reduce((acc, c) => {
      const url = MeasureService.getUrlForGet({
        site_id: siteId,
        t0: c.startDate,
        t1: c.endDate,
        device_ids: deviceIds,
      });
      acc[url] = c.campaign;
      return acc;
    }, {} as Record<string, string>);
  }, [campaigns, siteId, deviceIds]);
  const requirementsSelector = useMemo(
    () => Object.keys(urlToCampaign),
    [urlToCampaign]
  );
  const requirements = useAppSelector((state) =>
    requirementsSelector.map((url) => ({
      url,
      state: state.data_slice.datasetsByParams[url],
    }))
  );

  return StateDecoratorWithoutRef(requirements, () => {
    const results = requirements.map(({ state }) => state);
    assert(results.every(isCachedDataDataState));
    const measuresByCampaigns = (
      results as {
        _tag: 'data';
        type: 'cached';
        data: {
          data: Measure[];
        };
        id: string;
      }[]
    ).reduce(
      (acc, { id, data: { data } }) => ({
        ...acc,
        [urlToCampaign[id]]: groupMeasuresByDate(data),
      }),
      {} as Record<string, Record<string, Measure[]>>
    );
    if (campaigns.length === 0) {
      return <div>Pas de campagnes pour ce site</div>;
    }

    const rows = structureData(
      campaigns,
      measuresByCampaigns,
      siteId,
      devicesById,
      measurePointsById,
      measureTypesById
    );

    //Fix styles for sticky header
    const headerCellSx = {
      backgroundColor: 'rgb(248, 248, 248)',
      color: 'rgb(153, 153, 153)',
      borderBottom: '1px solid rgba(224, 224, 224, 1)',
    };

    return (
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: 'calc(100vh - 82px - 130px)',
          border: '1px solid rgba(224, 224, 224, 1)',
        }}
      >
        <Table
          stickyHeader
          aria-label="collapsible and customized table"
          // overwrite the default zebra pattern just for this one table
          sx={{
            '& tbody tr:nth-of-type(2n)': {
              backgroundColor: 'rgb(255, 255, 255)',
            },
            '& tbody tr:nth-of-type(3n)': {
              backgroundColor: 'rgb(248, 248, 248)',
            },
            '& tbody tr:nth-of-type(4n)': {
              backgroundColor: 'rgb(255, 255, 255)',
            },
            border: 'none',
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx} />
              <TableCell sx={headerCellSx}>Campagne</TableCell>
              <TableCell sx={headerCellSx}>Thématique</TableCell>
              <TableCell sx={headerCellSx} align="right">
                Date de saisie
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <Row key={row.campaign + row.inputDate.getTime()} row={row} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  });
}

type MeasureDataComponentHelperProps = Omit<
  CollapsibleTableProps,
  'dataAdditionCount'
>;

function MeasureDataComponentHelper({
  campaigns,
  devicesById,
  measurePointsById,
  measureTypesById,
  siteId,
}: MeasureDataComponentHelperProps) {
  const dispatch = useAppDispatch();
  const addDataModal = useModal();

  return (
    <div className={`sites`}>
      <div
        style={{
          backgroundColor: 'white',
          padding: '32px',
          height: '100%',
        }}
      >
        <Box sx={{ mb: '24px' }}>
          <PermissionDecoratorV2 item={'measure_data_add_button'}>
            <Button variant="contained" onClick={() => addDataModal.open()}>
              {' '}
              Ajouter des données
            </Button>
          </PermissionDecoratorV2>
        </Box>

        <CollapsibleTable
          siteId={siteId}
          campaigns={campaigns}
          devicesById={devicesById}
          measurePointsById={measurePointsById}
          measureTypesById={measureTypesById}
        />
      </div>

      {/* Form for adding data  */}
      {addDataModal.isOpen && (
        <ValueFormComponent
          title="Ajout de mesures"
          onCancel={addDataModal.close}
          isOpen={addDataModal.isOpen}
          onAfterInsert={() => {
            dispatch(
              resetCachedDataForUrlByPattern(`/measures/sites/${siteId}/`)
            );
          }}
          siteId={siteId}
        />
      )}
    </div>
  );
}

function MeasureDataComponent(_props: object, ref: LegacyRef<HTMLDivElement>) {
  const { site_id } = useParams<{
    site_id: string;
  }>();

  const thematics = useAppSelector(
    (state) => state.data_slice.externals.thematics
  );
  const campaigns = useAppSelector(
    (state) => state.data_slice.externals.campaigns
  );
  const devices = useAppSelector((state) => state.data_slice.externals.devices);
  const deviceSites = useAppSelector(
    (state) => state.data_slice.externals.site_devices
  );
  const measure_types = useAppSelector(
    (state) => state.data_slice.externals.measure_types
  );
  const measure_points = useAppSelector(
    (state) => state.data_slice.externals.measure_points
  );

  const campaignParams = { site_id: site_id! };

  return SiteDecorator(ref, () => {
    return StateDecorator(
      [
        thematics,
        { params: campaignParams, ...campaigns },
        devices,
        deviceSites,
        measure_types,
        measure_points,
      ],
      ref,
      () => {
        const campaignKey = getKeyFromParamsAndId(campaignParams, 'campaigns');
        assert(isDataState(thematics));
        assert(isDataState(campaigns.value[campaignKey]));
        assert(isDataState(devices));
        assert(isDataState(deviceSites));
        assert(isDataState(measure_types));
        assert(isDataState(measure_points));

        const campaignsForSite = Object.values(
          campaigns.value[campaignKey].data
        );

        const campaignsWithThematics: CampaignWithThematics[] =
          campaignsForSite.map((c) => ({
            campaignId: c.id,
            campaign: c.name,
            thematic: c.thematic_ids
              ? A.mapFilter(
                  c.thematic_ids,
                  (tId) => thematics.data[tId]?.name
                ).join(', ')
              : '--',
            startDate: c.start_date,
            endDate: c.end_date,
          }));

        const measurePointsForSite = R.filter(
          (mp) =>
            site_id !== undefined &&
            mp.device_site?.site_id === Number.parseInt(site_id),
          measure_points.data
        );

        const deviceSitesForSite = R.mapFilter(
          ({ device_site }) => (device_site !== null ? device_site : undefined),
          measurePointsForSite
        );

        const deviceIdsForSite = new Set(
          R.values(R.map((ds) => ds.device_id, deviceSitesForSite))
        );

        const devicesForSite = R.filter(
          (device) =>
            device.type === 'manual_sensor' && deviceIdsForSite.has(device.id),
          devices.data
        );

        return (
          <MeasureDataComponentHelper
            siteId={Number.parseInt(site_id!)}
            campaigns={campaignsWithThematics}
            devicesById={devicesForSite}
            measurePointsById={measurePointsForSite}
            measureTypesById={measure_types.data}
          />
        );
      }
    );
  });
}

export default forwardRef(MeasureDataComponent);
